import React, { useState } from "react";
import { useLazyQuery } from "@apollo/client";
import { GET_ALL_FIELDS } from "./graphql";
import Button from "components/base/Button";
import { InlineSpinner } from "components/base/Spinner";
import Errors from "components/base/Errors";
import { Alert } from "components/base/Toast";
import { Select } from "components/base/Form";
import { FiCopy, FiCheck } from "react-icons/fi";
import { fieldTypes } from "./utils/constants";

// Odoo字段查询面板组件
const OdooFieldsPanel = () => {
  // 预定义的Odoo模型选项
  const modelOptions = [
    "purchase.order",
    "purchase.order.line",
    "product.product",
    "product.supplierinfo",
    "res.partner",
    "sale.order",
    "stock.picking",
    "account.incoterms",
    "stock.move", // Inventory  WH/IN WH/OUT
    "mrp.production", // MO
  ];

  const [model, setModel] = useState("purchase.order");
  const [fieldDetails, setFieldDetails] = useState({});
  const [modelCopied, setModelCopied] = useState(false);
  const [copiedFieldName, setCopiedFieldName] = useState(null);
  const [fetchFields, { loading, error, data }] = useLazyQuery(GET_ALL_FIELDS, {
    fetchPolicy: "network-only", // 强制从网络获取，不使用缓存
    onCompleted: (data) => {
      if (data && data.allFields && data.allFields.length > 0) {
        try {
          // 后端直接返回对象数组，直接使用
          const fieldsMap = {};
          data.allFields.forEach((field) => {
            fieldsMap[field.name] = field;
          });
          setFieldDetails(fieldsMap);
          console.log("成功获取字段详情");
        } catch (e) {
          console.error("处理字段详情时出错:", e);
          Alert("error", `处理字段详情出错: ${e.message}`);
        }
      }
    },
    onError: (error) => {
      Alert("error", `查询出错: ${error.message}`);
    },
  });

  const handleFetchFields = () => {
    fetchFields({ variables: { model } });
  };

  // 复制模型名称到剪贴板
  const copyModelName = () => {
    navigator.clipboard.writeText(model).then(
      function () {
        setModelCopied(true);
        // 3秒后重置图标
        setTimeout(() => {
          setModelCopied(false);
        }, 3000);
      },
      function (err) {
        console.error("无法复制模型名称: ", err);
      },
    );
  };

  // 复制字段名称到剪贴板
  const copyFieldName = (fieldName) => {
    navigator.clipboard.writeText(fieldName).then(
      function () {
        setCopiedFieldName(fieldName);
        // 3秒后重置图标
        setTimeout(() => {
          setCopiedFieldName(null);
        }, 3000);
      },
      function (err) {
        console.error("无法复制字段名称: ", err);
      },
    );
  };

  return (
    <div className="card bg-white shadow-md rounded-lg p-6">
      <h2 className="text-xl font-bold mb-4">Odoo模型的所有字段</h2>
      <div className="flex items-center space-x-4 mb-6">
        <div className="flex-1 flex items-center">
          <Select
            value={model}
            onChange={(e) => setModel(e.target.value)}
            className="flex-grow"
          >
            {modelOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </Select>
        </div>
        <div className="flex space-x-2">
          <Button
            primary
            onClick={handleFetchFields}
            disabled={loading || !model}
          >
            {loading ? (
              <span className="flex items-center">
                <InlineSpinner size={16} text={null} color="#fff" />
                <span className="ml-2">查询中...</span>
              </span>
            ) : (
              "查询字段"
            )}
          </Button>

          <button
            onClick={copyModelName}
            className="p-2 bg-gray-100 hover:bg-gray-200 rounded transition-colors flex items-center justify-center"
            title="复制模型名称"
          >
            {modelCopied ? (
              <FiCheck className="text-green-600" />
            ) : (
              <FiCopy className="text-gray-600" />
            )}
          </button>
        </div>
      </div>

      {loading && (
        <div className="flex justify-center my-6">
          <InlineSpinner size={24} />
        </div>
      )}

      {error && <Errors error={error} />}

      {data && data.allFields && (
        <div className="mt-4">
          <p className="text-base font-semibold mb-3 ">
            字段列表 (共 {Object.keys(fieldDetails).length})
          </p>
          <div className="border rounded-lg overflow-hidden">
            <div className="max-h-[calc(100vh-400px)] overflow-y-auto p-4 bg-gray-50">
              {Object.keys(fieldDetails).length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {Object.entries(fieldDetails).map(
                    ([fieldName, fieldInfo], index) => (
                      <div
                        key={index}
                        className="bg-white p-3 rounded border shadow-sm hover:shadow-md transition-shadow"
                      >
                        <h3 className="text-md font-bold text-blue-600 mb-2">
                          {fieldInfo.string || fieldName}
                        </h3>
                        <div className="flex items-center mb-2 border-b pb-2">
                          <span className=" text-base ">{fieldName}</span>
                          <button
                            onClick={() => copyFieldName(fieldName)}
                            className="ml-2 p-1 bg-gray-100 hover:bg-gray-200 rounded transition-colors"
                            title="复制字段名称"
                          >
                            {copiedFieldName === fieldName ? (
                              <FiCheck className="text-green-600 text-xs" />
                            ) : (
                              <FiCopy className="text-gray-600 text-xs" />
                            )}
                          </button>
                        </div>
                        <div className="grid grid-cols-2 gap-1 text-xs">
                          <div className="text-gray-600">type:</div>
                          <div className="font-medium">
                            {fieldInfo.type || ""}
                          </div>

                          <div className="text-gray-600">searchable:</div>
                          <div className="font-medium">
                            {fieldInfo.searchable ? "true" : "false"}
                          </div>

                          <div className="text-gray-600">required:</div>
                          <div className="font-medium">
                            {fieldInfo.required ? "true" : "false"}
                          </div>

                          <div className="text-gray-600">readonly:</div>
                          <div className="font-medium">
                            {fieldInfo.readonly ? "true" : "false"}
                          </div>

                          <div className="text-gray-600">manual:</div>
                          <div className="font-medium">
                            {fieldInfo.manual ? "true" : "false"}
                          </div>

                          <div className="text-gray-600">store:</div>
                          <div className="font-medium">
                            {fieldInfo.store ? "true" : "false"}
                          </div>

                          <div className="text-gray-600">sortable:</div>
                          <div className="font-medium">
                            {fieldInfo.sortable ? "true" : "false"}
                          </div>

                          <div className="text-gray-600">help:</div>
                          <div className="font-medium">
                            {fieldInfo.help || ""}
                          </div>

                          {fieldInfo.depends &&
                            fieldInfo.depends.length > 0 && (
                              <>
                                <div className="text-gray-600">depends:</div>
                                <div className="font-medium">
                                  {Array.isArray(fieldInfo.depends)
                                    ? fieldInfo.depends.join(", ")
                                    : fieldInfo.depends}
                                </div>
                              </>
                            )}
                        </div>
                      </div>
                    ),
                  )}
                </div>
              ) : (
                <p className="text-gray-500 italic">没有找到字段</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OdooFieldsPanel;
